import { uniq } from 'es-toolkit';
import { create } from 'zustand';
import { Episode, EpisodeCompoundId, PodcastId } from '../models/podcasts';
import { FilterStoreState } from '../routes/podcast/filter-store';
import debugLog from '../utils/debug';
import { exposeOnWindow, isEpisodeIdentifier, isWithin } from '../utils/helpers';
import { getInitialValue, setLazyValue } from '../utils/localstorage';

/**
 * Temporary session or device-specific state. Most of it gets reset when app is closed & re-opened.
 * Some of it is stored in LocalStorage. But none of it is synced to API.
 */
type SessionStore = {
  isPageBehindVeil: boolean; // When top nav popup is open (or transitioning open/closed)
  previousRoute: string; // Empty string when no prior route
  isInApp: boolean;
  dismissedRecentEpisodeIds: EpisodeCompoundId[];
  dismissRecentEpisode: (episode: Episode) => void;
  lastActivationTimestamp: number | null;
  currentModal: 'feedback' | 'sign-in' | 'delete-account' | 'welcome' | null;
  recentAdSkips: `${EpisodeCompoundId}:${number}`[]; // EpisodeId & ad start time pairs
  trackRecentAdSkip: (episodeId: EpisodeCompoundId, startTime: number) => void;
  wasAdRecentlySkipped: (episodeId: EpisodeCompoundId, startTime: number) => boolean;
  podcastPageState: Partial<Record<PodcastId, FilterStoreState>>;
};

export const useSessionStore = create<SessionStore>()((set, get) => ({
  isPageBehindVeil: false,
  previousRoute: '',
  isInApp: true,
  dismissedRecentEpisodeIds: getInitialValue(
    'SessionStore__dismissedRecentEpisodeIds',
    (val) => Array.isArray(val) && val.every(isEpisodeIdentifier),
    []
  ),
  dismissRecentEpisode: (episode) => {
    const dismissedRecentEpisodeIds = [episode.id, ...get().dismissedRecentEpisodeIds];
    set({ dismissedRecentEpisodeIds });

    // Store up to 100 in LocalStorage
    setLazyValue(
      'SessionStore__dismissedRecentEpisodeIds',
      dismissedRecentEpisodeIds.slice(0, 100)
    );
  },
  lastActivationTimestamp: getInitialValue(
    'sw_activation_timestamp',
    (val: any) => Number.isInteger(val),
    isServiceWorkerActive() ? Date.now() : null
  ),
  currentModal: null,
  recentAdSkips: getInitialValue(
    'SessionStore__recentAdSkips',
    (val) =>
      Array.isArray(val) &&
      val.every((val) => {
        const [id, startTime] = (typeof val === 'string' ? val : '').split(':');
        return isEpisodeIdentifier(id) && !Number.isNaN(parseFloat(startTime));
      }),
    []
  ),
  trackRecentAdSkip: (episodeId: EpisodeCompoundId, startTime: number) => {
    // Keep track of 5 most recent ad-skips
    const idAndStartTime = `${episodeId}:${startTime}` as const;
    const recentAdSkips = uniq([idAndStartTime, ...get().recentAdSkips]).slice(0, 5);
    set({ recentAdSkips });
    setLazyValue('SessionStore__recentAdSkips', recentAdSkips);
  },
  wasAdRecentlySkipped: (episodeId: EpisodeCompoundId, startTime: number) =>
    get().recentAdSkips.some((idAndStartTime) => {
      const [thisEpisodeId, thisStartTime] = idAndStartTime.split(':');
      return (
        thisEpisodeId === episodeId &&
        // Allow for 5 second tolerance in case ad start times are different due to float -> double -> float conversions
        // or tweaks to ad-detection logic
        isWithin(startTime, parseFloat(thisStartTime), 5)
      );
    }),
  podcastPageState: {},
}));

export function updatePodcastPageState(podcastId: string, filters: FilterStoreState) {
  useSessionStore.setState({
    podcastPageState: {
      ...useSessionStore.getState().podcastPageState,
      [podcastId]: filters,
    },
  });
}

const { trackRecentAdSkip, wasAdRecentlySkipped } = useSessionStore.getState();
export { trackRecentAdSkip, wasAdRecentlySkipped };

const inAppRoutes = [
  '/now',
  '/library',
  '/search',
  '/history',
  '/pricing-in-app',
  '/downloads',
  '/account',
  '/delete-account',
  '/fallback-page-shell',
];
export function getUpdatedIsInApp(rootRelativeUrl: string) {
  if (!rootRelativeUrl || !rootRelativeUrl.startsWith('/'))
    throw new Error(`Invalid rootRelativeUrl: ${rootRelativeUrl}`);

  const route = rootRelativeUrl.split(/[?#]/)[0];
  return inAppRoutes.includes(route) || route.startsWith('/podcast/');
}

// Update timestamp when new service worker is activated
if (typeof navigator !== 'undefined' && navigator.serviceWorker) {
  navigator.serviceWorker.addEventListener('controllerchange', () => {
    debugLog({ logName: 'sw', logColor: 'indigo' }, 'Activated service worker');
    const timestamp = Date.now();
    useSessionStore.setState({ lastActivationTimestamp: timestamp });
    setLazyValue('sw_activation_timestamp', timestamp);
  });
}

export const useCurrentModal = () => useSessionStore((s) => s.currentModal);
export const showModal = (modalName: SessionStore['currentModal']) =>
  useSessionStore.setState({ currentModal: modalName });
export const hideModal = () => useSessionStore.setState({ currentModal: null });

function isServiceWorkerActive() {
  return Boolean(
    typeof navigator !== 'undefined' &&
      navigator.serviceWorker &&
      navigator.serviceWorker.controller
  );
}

exposeOnWindow({ useSessionStore });
